import {Tr} from "@/i18n/i18n";
import {injectable} from "inversify";
import {Keys} from "@/constants/key";
import {TaskPanel} from "@/scenes.ui/task-panel";
import {ListView} from "@/scenes.ui/list-view";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {EventRewardDto, Task, TaskType} from "@/store/task/types";
import {BlackoutUtils} from "@/utils/blackout-utils";
import {HintState, Player} from "@/store/player/types";
import SoundUtils from "@/utils/sound-utils";
import PlayerStore from "@/store/player";
import TaskStore from "@/store/task";
import SpriteUtils from "@/utils/sprite-utils";
import {TaskConstants} from "@/constants/task-constants";
import {Urls} from "@/constants/urls";
import CU from "../utils/c-u";
import {EventRewardPanel} from "@/scenes.ui/event-reward-panel";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import BaseSound = Phaser.Sound.BaseSound;

@injectable()
export class TaskService {

    private readonly DEPTH: number = 3000;
    private scene: Scene | undefined;
    private canvas: HTMLCanvasElement | undefined;

    private mainContainer: Container | undefined;

    private clickSound!: BaseSound | undefined;
    private awardSound!: BaseSound | undefined;
    private selectSound!: BaseSound | undefined;
    private levelUpSound!: BaseSound | undefined;

    private closeCallback!: () => unknown;
    private openEventInfoPageCallback!: () => unknown;

    public openTasks(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => void, openEventInfoPageCallback: () => void) {
        this.scene = scene;
        this.canvas = canvas;

        let self = this;

        this.closeCallback = closeCallback;
        this.openEventInfoPageCallback = openEventInfoPageCallback;

        BlackoutUtils.blackout(scene, canvas, () => {
            self.clickSound = SoundUtils.addSound(self.scene!, Keys.SOUND_BUTTON_CLICK);
            self.awardSound = SoundUtils.addSound(self.scene!, Keys.SOUND_AWARD);
            self.selectSound = SoundUtils.addSound(self.scene!, Keys.SOUND_SELECT);
            self.levelUpSound = SoundUtils.addSound(self.scene!, Keys.SOUND_LEVEL_UP);

            self.mainContainer = new Container(scene);
            self.mainContainer.setDepth(this.DEPTH);
            self.scene!.add.existing(self.mainContainer);

            const background = SpriteUtils.addSprite(self.scene!, 0, 0, Keys.TASK_BACK).setOrigin(0, 0).setInteractive();
            const bottom = SpriteUtils.addSprite(self.scene!, 0, canvas.height - 50, Keys.TASK_BOTTOM).setOrigin(0, 1).setInteractive();

            const titleLight = SpriteUtils.addSprite(self.scene!, 300, 210, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_TITLE_LIGHT).setOrigin(0.5, 0.5).setInteractive();
            const title = TextUtils.addTextByFamily(self.scene!, Tr('zadanija'), 300, 214, `${Urls.CURRENT_TEXT_STYLE}`, 24,
                'normal', 420, Fonts.BENGALY_NORMAL)
                .setOrigin(0.5, 0.5).setStroke('#000000', 1);

            let player: Player = PlayerStore.getPlayer();

            let tickets = TextUtils.addTextAdvanced(self.scene!, String(player.tickets), 550-5, 214,
                '#c3c3c3', 14, 'normal', 320, 'left', 1, Fonts.ARIAL_MT);
            tickets.setOrigin(1, 0.5).setStroke('#000000', 1);
            const ticketsIcon = SpriteUtils.addImage(self.scene!, 550, 214, Keys.IMG_TICKET, undefined, 0.8).setOrigin(0, 0.5);

            const xButton = SpriteUtils.addImage(self.scene!, CU.ETALON_CANVAS_WIDTH * 0.95, CU.ETALON_CANVAS_WIDTH * 0.05, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_X).setOrigin(0.5, 0.5);

            xButton.setInteractive().on('pointerup', function() {
                SoundUtils.play(self.selectSound);
                self.closeTasks();
            });

            const separatorTop = SpriteUtils.addSprite(self.scene!, 0, 230, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE).setOrigin(0, 0);

            const separatorBottom = SpriteUtils.addSprite(this.scene!, 0, this.canvas!.height - 110, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR_LARGE)
                .setOrigin(0, 0);

            let shopBottom = SpriteUtils.addSprite(scene!, CU.ETALON_CANVAS_WIDTH / 2,canvas!.height / CU.S, Keys.SHOP_BOTTOM)
                .setOrigin(0.5, 1);
            SpriteUtils.setDispSize(shopBottom, canvas!.width, canvas!.height);

            self.mainContainer.add([shopBottom, background, bottom, xButton, tickets, ticketsIcon, titleLight, title, separatorTop, separatorBottom]);

            if (player.tasksAvailable) {
                self.init();
            } else {

                const back = SpriteUtils.addImage(self.scene!, 0, 240, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_PANEL_BACK).setOrigin(0, 0);
                SpriteUtils.setDispSize(back, CU.CANVAS_WIDTH, this.canvas!.height - 343);

                const noTasks = TextUtils.addTextByFamily(self.scene!, Tr('zadaniy.poka.net'), 300, (this.canvas!.height + 343 - 240) / 2, '#cac8c3', 24,
                    'italic', 420, Fonts.BENGALY_NORMAL).setStroke('#000000', 1);

                /** Добавление отступа для корректировки обрезания букв
                 * По неизвестной причине некоторые крайние буквы обрезаются.
                 * Проблема в шрифтах скорее всего.
                 * Решается добавлением паддинга.
                 * */
                noTasks.setPadding(10);

                self.mainContainer.add([back, noTasks]);
            }

            TaskStore.setTasksSeen();
        });
    }

    public static promptHint(player: Player):boolean{
        return player.versionStart >= 3 && player.hintState == HintState.UNSEEN;
    }

    public static showHint(player: Player):boolean{
        return player.versionStart >= 3 && (player.hintState === HintState.UNSEEN || player.hintState === HintState.SEEN);
    }

    private async init(): Promise<void> {
        let panels: TaskPanel[] = [];

        const self = this;
        let player: Player = await PlayerStore.fetchPlayer().then(() => {
            return PlayerStore.getPlayer();
        });

        let tasks: Task[] = Object.assign([], player.tasks).sort((a: Task, b: Task) => {
            if (a.taskId === TaskConstants.TASK_EASTER_EGG) {
                return -1;
            } else if (b.taskId === TaskConstants.TASK_EASTER_EGG) {
                return 1;
            }

            if (a.taskId === TaskConstants.TASK_FERN) {
                return -1;
            } else if (b.taskId === TaskConstants.TASK_FERN) {
                return 1;
            }

            if (a.taskId === TaskConstants.TASK_APPLE) {
                return -1;
            } else if (b.taskId === TaskConstants.TASK_APPLE) {
                return 1;
            }

            if (a.taskId === TaskConstants.TASK_BOOKS) {
                return -1;
            } else if (b.taskId === TaskConstants.TASK_BOOKS) {
                return 1;
            }

            if (a.taskId === TaskConstants.TASK_PUMPKIN) {
                return -1;
            } else if (b.taskId === TaskConstants.TASK_PUMPKIN) {
                return 1;
            }

            if (a.taskId === TaskConstants.TASK_BIRTHDAY_HAT) {
                return -1;
            } else if (b.taskId === TaskConstants.TASK_BIRTHDAY_HAT) {
                return 1;
            }

            if (b.progress === b.total) {
                if (b.taskType == TaskType.DAILY && b.awardReceived) {
                    return -1;
                }
                return 1;
            }
            if (a.progress === a.total) {
                if (a.taskType == TaskType.DAILY && a.awardReceived) {
                    return 1;
                }
                return -1;
            }
            return (b.taskType == TaskType.SCENARIO ? 4 : 0) + (b.taskType == TaskType.SINGLE ? 2 : 0) + (b.taskType == TaskType.DAILY ? 1 : 0) + b.progress / b.total
                - (a.taskType == TaskType.SCENARIO ? 4 : 0) - (a.taskType == TaskType.SINGLE ? 2 : 0) - (a.taskType == TaskType.DAILY ? 1 : 0) - a.progress / a.total;
        });

        for (let t of tasks) {
            if (!TaskPanel.isTaskIdProcessing(t)) {
                panels.push(new TaskPanel(this.scene!, this.canvas!, t, () => {
                    TaskStore.completeTask(t.taskId).then(() => {
                        self.init();
                        SoundUtils.play(self.clickSound);
                        new FeedbackPanel(self.scene!, Tr('nagrada.poluchena'), self.mainContainer!, true).show();
                    });
                }, () => self.openEventInfoPageCallback()));
            }
        }

        let listView: ListView = new ListView(this.scene!, this.canvas!, panels, 0, 240 * CU.S, CU.CANVAS_WIDTH, (this.canvas!.height - 343 * CU.S));

        this.mainContainer!.add([listView]);

        TaskStore.tryToTakeEventReward().then((rewardDto: EventRewardDto[]) => {
            if (rewardDto.length > 0) {
                self.scene!.load.image(Keys.WHEEL_SPLASH, 'assets/wheel/splash.png');
                setTimeout(() => {
                    SoundUtils.play(this.awardSound);
                    new EventRewardPanel(this.scene!, this.canvas!, rewardDto).open();
                }, 1000);
            }
        });
    }

    private closeTasks(): void {
        if (this.mainContainer && this.mainContainer.active) {
            if (this.closeCallback != null) {
                this.closeCallback();
            }
            this.mainContainer!.setVisible(false);
            this.mainContainer!.destroy();
        }
    }
}

